import React, { createContext, useContext, useState, useEffect } from 'react';

// Define Types
export type Role = 'admin' | 'collaborator' | 'celular';

export interface User {
  id: string;
  username: string;
  role: Role;
  name: string;
  photoUrl?: string;
  active: boolean;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (username: string, password: string) => Promise<{ success: boolean; error?: string; user?: User }>;
  logout: () => void;
  refreshUser: (updatedData: Partial<User>) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Mock Database / Seed Data (Movemos para o mockBackend para persistencia em tempo de execução, 
// mas mantemos a logica de login aqui lendo do localStorage ou mock)
export const MOCK_INITIAL_USERS = [
  { username: 'admin', password: '1010', role: 'admin' as Role, id: '1', name: 'Super Administrador', active: true, photoUrl: '' },
  { username: 'user', password: 'user', role: 'collaborator' as Role, id: '2', name: 'Colaborador', active: true, photoUrl: '' },
  { username: 'celular', password: '123', role: 'celular' as Role, id: '3', name: 'Usuário Mobile', active: true, photoUrl: '' }
];

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Helper para ler cookie
  const getCookie = (name: string) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop()?.split(';').shift();
    return null;
  };

  // Helper para setar cookie
  const setCookie = (name: string, value: string, days: number) => {
    let expires = "";
    if (days) {
      const date = new Date();
      date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
      expires = "; expires=" + date.toUTCString();
    }
    document.cookie = name + "=" + (value || "")  + expires + "; path=/; SameSite=None; Secure";
  };

  // Initialize Session from LocalStorage or Cookie
  useEffect(() => {
    let storedUser = localStorage.getItem('acme_auth_user');
    
    if (!storedUser) {
      const cookieUser = getCookie('acme_auth_user');
      if (cookieUser) {
        storedUser = decodeURIComponent(cookieUser);
        localStorage.setItem('acme_auth_user', storedUser);
      }
    }

    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (e) {
        localStorage.removeItem('acme_auth_user');
        setCookie('acme_auth_user', '', -1);
      }
    }
    setIsLoading(false);
  }, []);

  const login = async (username: string, password: string) => {
    try {
      const res = await fetch('/api/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      });

      if (res.ok) {
        const contentType = res.headers.get('content-type');
        if (contentType && contentType.includes('text/html')) {
          return { success: false, error: 'Erro de configuração no servidor (VPS): A API retornou HTML em vez de JSON. Certifique-se de rodar o backend com "npm start" (NODE_ENV=production) e não apenas servir a pasta dist estaticamente.' };
        }
        const userData = await res.json();
        setUser(userData);
        localStorage.setItem('acme_auth_user', JSON.stringify(userData));
        setCookie('acme_auth_user', encodeURIComponent(JSON.stringify(userData)), 30);
        return { success: true, user: userData };
      } else {
        const contentType = res.headers.get('content-type');
        if (contentType && contentType.includes('text/html')) {
          return { success: false, error: 'Erro 404/500 no servidor (VPS): A API não foi encontrada ou falhou. Verifique se o backend está rodando na mesma porta.' };
        }
        const errorData = await res.json();
        return { success: false, error: errorData.error || 'Credenciais inválidas. Verifique seu usuário e senha.' };
      }
    } catch (error) {
      console.error("Login fetch error:", error);
      return { success: false, error: 'Erro de conexão com o servidor. Verifique o console para mais detalhes.' };
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('acme_auth_user');
    setCookie('acme_auth_user', '', -1);
  };

  // Permite atualizar o estado local do usuário sem relogar (ex: mudou foto)
  const refreshUser = (updatedData: Partial<User>) => {
    if (user) {
      const newUser = { ...user, ...updatedData };
      setUser(newUser);
      localStorage.setItem('acme_auth_user', JSON.stringify(newUser));
      setCookie('acme_auth_user', encodeURIComponent(JSON.stringify(newUser)), 30);
    }
  };

  return (
    <AuthContext.Provider value={{ user, isAuthenticated: !!user, isLoading, login, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
};